import { useState } from "react";
import { Link, useNavigate } from "react-router";
import { Loader2 } from "lucide-react";

import { AuthCard } from "@/components/auth/AuthCard";
import { PUBLIC_LINK } from "@/components/public/control-classes";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Skeleton } from "@/components/ui/skeleton";
import { useAuth } from "@/contexts/AuthContext";
import { useTenantPresentation } from "@/hooks/use-tenant-presentation";

/**
 * Set a new password after following the recovery email link. supabase-js
 * consumes the recovery token from the URL and fires PASSWORD_RECOVERY (→
 * `isRecovery`); an already-signed-in user may also change their password here.
 */
export function ResetPasswordPage() {
  const {
    isAuthReady,
    isRecovery,
    session,
    authInitializationError,
    updatePassword,
  } = useAuth();
  const { name, logoUrl } = useTenantPresentation();
  const navigate = useNavigate();
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  const canReset = isRecovery || !!session;

  const onSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    if (password.length < 8) {
      setError("Password must be at least 8 characters.");
      return;
    }
    if (password !== confirm) {
      setError("Passwords do not match.");
      return;
    }
    setSubmitting(true);
    const { error: updateError } = await updatePassword(password);
    setSubmitting(false);
    if (updateError) {
      setError(updateError);
      return;
    }
    navigate("/", { replace: true });
  };

  if (!isAuthReady) {
    return (
      <AuthCard
        brandName={name}
        logoUrl={logoUrl}
        subtitle="Password recovery"
        eyebrow="Password recovery"
        heading="Verifying your reset link"
        lede="This should only take a moment."
      >
        {/* The form's shape stands in for the form, so the card does not
            jump when the link verifies; the status line carries the wait. */}
        <div className="grid gap-4" aria-busy="true">
          <p role="status" className="flex items-center gap-3 text-ui text-muted-foreground">
            <Loader2 className="size-4 animate-spin" aria-hidden="true" />
            Securely opening your password reset…
          </p>
          <div className="grid gap-2" aria-hidden="true">
            <Skeleton className="h-4 w-28" />
            <Skeleton className="h-[var(--control-height)]" />
          </div>
          <div className="grid gap-2" aria-hidden="true">
            <Skeleton className="h-4 w-36" />
            <Skeleton className="h-[var(--control-height)]" />
          </div>
          <Skeleton className="h-[var(--control-height)]" aria-hidden="true" />
          <Link
            to="/forgot-password?method=recovery-code"
            className={`${PUBLIC_LINK} justify-self-start text-ui text-primary hover:underline`}
          >
            Enter recovery code instead
          </Link>
        </div>
      </AuthCard>
    );
  }

  if (!canReset) {
    return (
      <AuthCard
        brandName={name}
        logoUrl={logoUrl}
        subtitle="Password recovery"
        eyebrow="Password recovery"
        heading="Reset link unavailable"
        lede="Use the recovery code from your email or request a fresh message."
      >
        <div className="grid gap-4">
          <p className="text-ui text-foreground">
            {authInitializationError
              ? "We couldn't verify this reset link. It may have expired or been opened in a different browser."
              : "This reset link is invalid or has expired."}
          </p>
          <div className="grid gap-3 sm:grid-cols-2">
            <Button asChild className="w-full">
              <Link to="/forgot-password?method=recovery-code">Enter recovery code</Link>
            </Button>
            <Button asChild variant="outline" className="w-full">
              <Link to="/forgot-password">Request a new email</Link>
            </Button>
          </div>
        </div>
      </AuthCard>
    );
  }

  return (
    <AuthCard
      brandName={name}
      logoUrl={logoUrl}
      subtitle="Password recovery"
      eyebrow="Password recovery"
      heading="Choose a new password"
      lede="Use at least 8 characters."
    >
      <form onSubmit={onSubmit} className="grid gap-4" noValidate>
        {error && (
          <Alert variant="destructive" id="reset-password-error">
            <AlertDescription>{error}</AlertDescription>
          </Alert>
        )}
        <div className="grid gap-2">
          <Label htmlFor="password">New password</Label>
          <Input
            id="password"
            type="password"
            autoComplete="new-password"
            placeholder="••••••••"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            required
            aria-invalid={error ? true : undefined}
            aria-describedby={error ? "reset-password-error" : undefined}
          />
        </div>
        <div className="grid gap-2">
          <Label htmlFor="confirm">Confirm password</Label>
          <Input
            id="confirm"
            type="password"
            autoComplete="new-password"
            placeholder="••••••••"
            value={confirm}
            onChange={(e) => setConfirm(e.target.value)}
            required
            aria-invalid={error ? true : undefined}
            aria-describedby={error ? "reset-password-error" : undefined}
          />
        </div>
        <Button type="submit" className="w-full" disabled={submitting}>
          {submitting && <Loader2 className="size-4 animate-spin" aria-hidden="true" />}
          Update password
        </Button>
      </form>
    </AuthCard>
  );
}
